import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchDocumentBlob, splitDocument, type SplitDefinition } from "../api";
import type { Document } from "../types/api";
import {
  colorForPartIndex,
  pageSetToRanges,
  pagesToRangesText,
  parseRangesToPages,
} from "./splitRanges";
import "./SplitDocumentModal.css";

// react-pdf lazy-loaded — the pdfjs worker + library bundle is ~1 MB and
// only needed for PDFs, so we don't pay for it on non-PDF splits.
const PdfThumbnailGrid = lazy(() =>
  import("./PdfThumbnailGrid").then((m) => ({ default: m.PdfThumbnailGrid })),
);

interface Part {
  /** Local key stable across renders, used as React key. */
  key: string;
  title: string;
  /** Canonical set of pages this part claims. Clicks + text edits both
   *  converge to this — the text input is a formatted view of it. */
  pages: Set<number>;
  /** Editable text form. Held separately from `pages` while the user is
   *  actively typing, then re-parsed on blur. `pagesToRangesText(pages)`
   *  is the equilibrium value. */
  rangesText: string;
  /** True while the user has typed text that hasn't been parsed back
   *  into `pages` yet — used to decide whether a re-render should
   *  overwrite their draft. */
  rangesTextDirty: boolean;
  /** Non-null when the current `rangesText` failed to parse. */
  rangesError: string | null;
}

/** Shape the queue's split-suggestion feature seeds the modal with — a
 *  list of proposed parts the user then edits before submitting. Kept
 *  as plain strings so the modal owns all parsing (empty pages, invalid
 *  ranges, etc. all funnel through the existing validators). */
export interface InitialPart {
  pages: string;
  title?: string;
}

interface Props {
  doc: Document;
  /** Estimated total page count when known; otherwise inferred from the
   *  PDF once react-pdf loads it, or asked from the user for non-PDFs. */
  initialPageCount?: number | null;
  /**
   * Pre-fill the parts list — used when the modal is opened from a
   * split-suggestion queue item. Omitted → two empty parts (today's
   * behaviour, preserved for all existing callers).
   */
  initialParts?: InitialPart[];
  onClose: () => void;
  onSuccess: () => void;
}

let seq = 0;
const partKey = () => `s${++seq}`;

function emptyPart(): Part {
  return {
    key: partKey(),
    title: "",
    pages: new Set(),
    rangesText: "",
    rangesTextDirty: false,
    rangesError: null,
  };
}

export function SplitDocumentModal({
  doc,
  initialPageCount,
  initialParts,
  onClose,
  onSuccess,
}: Props) {
  const isPdf = doc.mime_type === "application/pdf";

  const metadataPageCount =
    typeof doc.metadata?.pageCount === "number"
      ? (doc.metadata.pageCount as number)
      : null;
  const [pageCount, setPageCount] = useState<number | null>(
    initialPageCount ?? metadataPageCount ?? null,
  );

  // Seed parts: initialParts (from the queue's AI split suggestion) wins
  // when provided; otherwise fall back to today's two-empty-parts default.
  // The parseRangesToPages helper handles bad input by dropping it into
  // rangesError, so a malformed suggested part still renders — the user
  // sees the error and can fix it before submitting.
  const [parts, setParts] = useState<Part[]>(() => {
    if (initialParts && initialParts.length > 0) {
      return initialParts.map((p) => {
        const { pages, error } = parseRangesToPages(p.pages);
        return {
          key: partKey(),
          title: p.title?.trim() ?? "",
          pages,
          rangesText: p.pages,
          rangesTextDirty: false,
          rangesError: error,
        };
      });
    }
    return [emptyPart(), emptyPart()];
  });
  const [focusedKey, setFocusedKey] = useState<string>(() => "");
  useEffect(() => {
    // Focus the first part by default, and never lose focus if the focused
    // part is removed.
    if (!parts.find((p) => p.key === focusedKey) && parts.length > 0) {
      setFocusedKey(parts[0].key);
    }
  }, [parts, focusedKey]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PDF blob — only fetched for PDF docs. Failure falls back to text-only.
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  useEffect(() => {
    if (!isPdf) return;
    let cancelled = false;
    fetchDocumentBlob(doc.id)
      .then((blob) => {
        if (!cancelled) setPdfBlob(blob);
      })
      .catch(() => {
        if (!cancelled)
          setPdfError(
            "Could not load the PDF preview — you can still enter page ranges below.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [doc.id, isPdf]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, submitting]);

  // ── Part mutations ─────────────────────────────────────────────────────

  const addPart = useCallback(() => {
    setParts((prev) => {
      const next = [...prev, emptyPart()];
      return next;
    });
  }, []);

  const removePart = useCallback((key: string) => {
    setParts((prev) => (prev.length <= 1 ? prev : prev.filter((p) => p.key !== key)));
  }, []);

  /** Update a single part in-place. */
  const patchPart = useCallback(
    (key: string, patch: (p: Part) => Part) => {
      setParts((prev) => prev.map((p) => (p.key === key ? patch(p) : p)));
    },
    [],
  );

  /** Assign or unassign a page in the currently-focused part. Also
   *  removes the page from any OTHER part that had claimed it, so a
   *  click on an already-claimed page moves ownership rather than
   *  producing an overlap the validator would reject. */
  const togglePageInFocused = useCallback(
    (pageNumber: number) => {
      setParts((prev) => {
        const focused = prev.find((p) => p.key === focusedKey);
        if (!focused) return prev;
        const focusedHas = focused.pages.has(pageNumber);
        return prev.map((p) => {
          if (p.key === focusedKey) {
            const nextPages = new Set(p.pages);
            if (focusedHas) nextPages.delete(pageNumber);
            else nextPages.add(pageNumber);
            return {
              ...p,
              pages: nextPages,
              rangesText: pagesToRangesText(nextPages),
              rangesTextDirty: false,
              rangesError: null,
            };
          }
          // Not the focused part: strip the page if it was owned here
          // (only when we're assigning to focused, not when we're
          // un-assigning from focused).
          if (!focusedHas && p.pages.has(pageNumber)) {
            const nextPages = new Set(p.pages);
            nextPages.delete(pageNumber);
            return {
              ...p,
              pages: nextPages,
              rangesText: pagesToRangesText(nextPages),
              rangesTextDirty: false,
              rangesError: null,
            };
          }
          return p;
        });
      });
    },
    [focusedKey],
  );

  /** Called on every keystroke in a range text input — updates the draft
   *  text and re-parses eagerly for validation feedback, without
   *  committing the parsed pages until blur (so a mid-typing "1-" doesn't
   *  wipe the visual selection). */
  const editRangesText = useCallback(
    (key: string, text: string) => {
      const { error } = parseRangesToPages(text);
      patchPart(key, (p) => ({
        ...p,
        rangesText: text,
        rangesTextDirty: true,
        rangesError: error,
      }));
    },
    [patchPart],
  );

  /** Commit the text into the pages set. Called on blur. */
  const commitRangesText = useCallback(
    (key: string) => {
      patchPart(key, (p) => {
        if (!p.rangesTextDirty) return p;
        const { pages, error } = parseRangesToPages(p.rangesText);
        if (error) return p; // keep dirty text and error visible
        return {
          ...p,
          pages,
          rangesText: pagesToRangesText(pages),
          rangesTextDirty: false,
          rangesError: null,
        };
      });
    },
    [patchPart],
  );

  // ── Derived state ──────────────────────────────────────────────────────

  /** page → color for the currently-claiming part. Used by the grid. */
  const colorForPage: Record<number, string | undefined> = useMemo(() => {
    const out: Record<number, string | undefined> = {};
    parts.forEach((p, idx) => {
      const color = colorForPartIndex(idx);
      p.pages.forEach((n) => {
        out[n] = color;
      });
    });
    return out;
  }, [parts]);

  /** Pages the user hasn't assigned yet — only shown when we know the total. */
  const unassignedPages = useMemo(() => {
    if (pageCount == null) return null;
    const claimed = new Set<number>();
    parts.forEach((p) => p.pages.forEach((n) => claimed.add(n)));
    const missing: number[] = [];
    for (let n = 1; n <= pageCount; n++) {
      if (!claimed.has(n)) missing.push(n);
    }
    return missing;
  }, [parts, pageCount]);

  const anyPartHasPages = parts.some((p) => p.pages.size > 0);
  const anyRowError = parts.some((p) => p.rangesError != null);

  const submitDisabled = submitting || !anyPartHasPages || anyRowError;

  const handleSubmit = useCallback(async () => {
    if (submitDisabled) return;
    setSubmitting(true);
    setError(null);
    try {
      const splits: SplitDefinition[] = parts
        .filter((p) => p.pages.size > 0)
        .map((p) => ({
          title: p.title.trim() || undefined,
          pageRanges: pageSetToRanges(p.pages),
        }));
      await splitDocument(doc.id, splits);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Split failed");
    } finally {
      setSubmitting(false);
    }
  }, [doc.id, parts, submitDisabled, onSuccess]);

  // Track whether the PDF actually rendered — used to show a fallback
  // hint if the grid failed and we're operating on text alone.
  const pdfLoadFailedRef = useRef(false);
  const handlePdfLoad = useCallback((n: number) => setPageCount(n), []);
  const handlePdfError = useCallback((err: Error) => {
    pdfLoadFailedRef.current = true;
    // Surface the real reason to the console. Without this the fallback
    // banner appears but there's no clue what pdfjs actually rejected —
    // could be an invalid blob, a missing worker chunk, a CMap fetch
    // that CSP blocked, a version-mismatch API error, etc.
    console.error("[SplitDocumentModal] PDF render failed:", err);
    setPdfError(
      "Could not render the PDF — you can still enter page ranges below.",
    );
  }, []);

  const showPdfPane = isPdf && !pdfError;

  return (
    <div
      className="split-modal-overlay"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className={`split-modal ${showPdfPane ? "split-modal--with-pdf" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="split-modal-title"
      >
        <header className="split-modal__header">
          <div>
            <h3 id="split-modal-title" className="split-modal__title">
              Split PDF
            </h3>
            <p className="split-modal__subtitle" title={doc.original_name}>
              {doc.original_name}
              {pageCount != null && (
                <span className="split-modal__page-badge">
                  {pageCount} pages
                </span>
              )}
            </p>
          </div>
          <button
            className="split-modal__close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="split-modal__body">
          {/* ── LEFT COLUMN: PDF thumbnail grid (PDFs only) ── */}
          {showPdfPane && (
            <div className="split-modal__pdf-pane">
              {pdfBlob ? (
                <Suspense
                  fallback={<div className="pdf-grid__status">Loading viewer…</div>}
                >
                  <PdfThumbnailGrid
                    blob={pdfBlob}
                    colorForPage={colorForPage}
                    onLoad={handlePdfLoad}
                    onPageClick={togglePageInFocused}
                    onLoadError={handlePdfError}
                  />
                </Suspense>
              ) : (
                <div className="pdf-grid__status">Loading PDF…</div>
              )}
              <div className="split-modal__pdf-hint">
                Click a page to add it to the highlighted part on the right.
                Clicking a page that's already in another part moves it.
              </div>
            </div>
          )}

          {/* ── RIGHT COLUMN: parts editor ── */}
          <div className="split-modal__parts-pane">
            {pdfError && (
              <div className="split-modal__warning" role="status">
                {pdfError}
              </div>
            )}

            {/* Manual page-count fallback only when we don't have the PDF
                 or metadata to tell us. */}
            {pageCount == null && (
              <div className="split-modal__page-count">
                <label htmlFor="split-pagecount">Total pages in this document</label>
                <input
                  id="split-pagecount"
                  type="number"
                  min={1}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setPageCount(Number.isFinite(n) && n > 0 ? n : null);
                  }}
                  placeholder="e.g. 12"
                  disabled={submitting}
                />
                <span className="split-modal__hint">
                  Optional — used for the unassigned-pages check.
                </span>
              </div>
            )}

            <ol className="split-modal__parts">
              {parts.map((p, i) => {
                const color = colorForPartIndex(i);
                const isFocused = p.key === focusedKey;
                return (
                  <li
                    key={p.key}
                    className={`split-modal__part ${isFocused ? "split-modal__part--focused" : ""}`}
                    style={isFocused ? { borderColor: color } : undefined}
                    onClickCapture={() => setFocusedKey(p.key)}
                  >
                    <div className="split-modal__part-head">
                      <span
                        className="split-modal__part-swatch"
                        style={{ background: color }}
                        aria-hidden="true"
                      />
                      <span className="split-modal__part-num">Part {i + 1}</span>
                      <span className="split-modal__part-count">
                        {p.pages.size} {p.pages.size === 1 ? "page" : "pages"}
                      </span>
                      <button
                        type="button"
                        className="split-modal__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePart(p.key);
                        }}
                        disabled={submitting || parts.length <= 1}
                        aria-label={`Remove part ${i + 1}`}
                      >
                        Remove
                      </button>
                    </div>
                    <label className="split-modal__field">
                      <span>Title (optional)</span>
                      <input
                        type="text"
                        value={p.title}
                        onChange={(e) =>
                          patchPart(p.key, (pp) => ({ ...pp, title: e.target.value }))
                        }
                        placeholder={`${stripPdf(doc.original_name)}-part-${i + 1}`}
                        disabled={submitting}
                      />
                    </label>
                    <label className="split-modal__field">
                      <span>Pages</span>
                      <input
                        type="text"
                        value={p.rangesText}
                        onChange={(e) => editRangesText(p.key, e.target.value)}
                        onBlur={() => commitRangesText(p.key)}
                        placeholder="Click pages on the left, or type e.g. 1-3, 5, 8-10"
                        disabled={submitting}
                        aria-invalid={!!p.rangesError}
                      />
                      {p.rangesError && (
                        <span className="split-modal__row-error">{p.rangesError}</span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ol>

            <button
              type="button"
              className="split-modal__add"
              onClick={addPart}
              disabled={submitting}
            >
              + Add another part
            </button>

            {unassignedPages && unassignedPages.length > 0 && (
              <div className="split-modal__warning" role="status">
                {unassignedPages.length === 1 ? (
                  <>Page {unassignedPages[0]} isn't in any part.</>
                ) : unassignedPages.length <= 8 ? (
                  <>Pages {unassignedPages.join(", ")} aren't in any part.</>
                ) : (
                  <>
                    {unassignedPages.length} pages aren't in any part
                    (e.g. {unassignedPages.slice(0, 5).join(", ")}…).
                  </>
                )}
                <span className="split-modal__warning-hint">
                  You can still submit — unassigned pages won't appear in any
                  new document.
                </span>
              </div>
            )}
            {error && (
              <div className="split-modal__error" role="alert">
                {error}
              </div>
            )}
          </div>
        </div>

        <footer className="split-modal__footer">
          <button
            type="button"
            className="split-modal__btn split-modal__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="split-modal__btn split-modal__btn--primary"
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {(() => {
              const nonEmpty = parts.filter((p) => p.pages.size > 0).length;
              if (submitting) return "Splitting…";
              if (nonEmpty === 0) return "Add pages to at least one part";
              return `Create ${nonEmpty} document${nonEmpty === 1 ? "" : "s"}`;
            })()}
          </button>
        </footer>
      </div>
    </div>
  );
}

function stripPdf(name: string): string {
  return name.replace(/\.pdf$/i, "");
}
